import { Alert, Box, Button, Divider, Flex, Grid, GridItem, Heading, SimpleGrid, Spacer, Text } from "@chakra-ui/react";
import Number from "components/number-format";
import moment from "moment";
import { FC, ReactElement, useState, useEffect, useRef } from "react";
import OrderService from "services/orders/order-service";
import TimerCountdown from "../../components/timer-countdown";
import AppConsts from "../../libs/appconst";
import PayProgram from "./pay-program";
import Midtrans from "components/midtrans";

declare global {
    interface Window { snap: any; }
}

interface PayDetailSceneProps {
    order: any;
};

const PayDetailScene: FC<PayDetailSceneProps> = ({ order }): ReactElement => {
    const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
    const [snapReady, setSnapReady] = useState<boolean>(false);
    const waitRef = useRef<number | null>(null);

    const ensureSnapReady = (timeoutMs = 10000, stepMs = 100): Promise<void> => {
        return new Promise((resolve, reject) => {
            const started = Date.now();
            const check = () => {
                if (window?.snap && typeof window.snap.pay === "function") {
                    setSnapReady(true); resolve(); return;
                }
                if (Date.now() - started >= timeoutMs) { reject(new Error("Midtrans Snap belum siap")); return; }
                waitRef.current = window.setTimeout(check, stepMs);
            };
            check();
        });
    };

    useEffect(() => {
        let cancelled = false;
        ensureSnapReady().catch(() => { if (!cancelled) setSnapReady(false); });
        return () => { cancelled = true; if (waitRef.current) window.clearTimeout(waitRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // auto-trigger pay (status 10 = menunggu bayar; paymentMethod 20 = midtrans)
    useEffect(() => {
        if (order && order.paymentMethod === 20 && order.status === 10 && snapReady) onSubmit();
    }, [order, snapReady]);

    const onSubmit = () => {
        setIsSubmiting(true);

        if (order.paymentMethod === 20) {
            OrderService.GenerateMidtransToken(order.id).then((res: any) => {
                var token = res.result.midtransToken;
                window.snap.pay(token);
            }).finally(() => {
                setIsSubmiting(false);
            });
        }
    };

    return (
        <>
            <Midtrans />
            <Grid templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(3, 1fr)' }} gap={{ base: 0, md: 5 }} mt={4}>
                <GridItem colSpan={2}>
                    <Box p={{ base: 2, md: 6 }} borderRadius={8} borderStyle={"solid"} borderWidth={{ base: 0, md: 1 }} borderColor={"blackAlpha.300"} textAlign={"center"}>
                        <Heading fontSize={{ base: 'md', md: "lg" }}>Segera Selesaikan Pembayaran Anda</Heading>
                        <Divider my={4} />
                        <Text fontSize={{ base: 'sm', md: "lg" }} textAlign={"center"}>Lakukan Pembayaran sebelum <span color={'teal'}>{moment(order.expiryDate).format("DD MMMM yyyy HH:mm A")}</span></Text>
                        <TimerCountdown expiryTimestamp={moment(order.expiryDate)} includeDays={false} />
                        <Divider my={4} />
                        <Text fontSize={{ base: 'md', md: "xl" }} textAlign={"center"}>Jumlah yang harus dibayar</Text>
                        <Heading fontSize={{ base: '2xl', md: "4xl" }} fontWeight={"bold"} textAlign={"center"} mt={2} color={'teal'}>
                            <Number value={order.amount} prefix={'Rp '} />
                        </Heading>
                        <Divider my={6} />
                        <Text fontSize={{ base: 'md', md: "lg" }} textAlign={"center"} fontWeight={"semibold"}>Detail Orderan Anda:</Text>
                        <Flex mt={4}>
                            <Spacer />
                            <Box >
                                <Alert status='warning' rounded={'lg'} p={{ base: 4, md: 6 }} pl={{ base: 4, md: 12 }}>
                                    <Box textAlign={"left"} fontSize={"sm"}>
                                        <Box mt={0}>
                                            <SimpleGrid columns={[2, null, 2]} columnGap={8}>
                                                <Box>Invoice No: </Box>
                                                <Box fontWeight="semibold"> {order.invoiceNo} </Box>
                                            </SimpleGrid>
                                            <SimpleGrid columns={[2, null, 2]} columnGap={8} mt={1} >
                                                <Box>Tanggal Transaksi: </Box>
                                                <Box fontWeight="semibold"> {moment(order.creationTime).format("DD MMM yyyy HH:mm")} </Box>
                                            </SimpleGrid>
                                            <SimpleGrid columns={[2, null, 2]} columnGap={8} mt={1} >
                                                <Box>Total: </Box>
                                                <Box fontWeight="semibold">
                                                    <Number value={order.amount} prefix={'Rp '} />
                                                </Box>
                                            </SimpleGrid>
                                            <SimpleGrid columns={[2, null, 2]} columnGap={8} mt={1} >
                                                <Box>Status: </Box>
                                                <Box fontWeight="semibold"> {order.statusString} </Box>
                                            </SimpleGrid>
                                        </Box>
                                    </Box>
                                </Alert>
                            </Box>
                            <Spacer />
                        </Flex>

                        {
                            (order.status === 10 || order.status === 40) &&
                            <>
                                <Divider my={4} />
                                <Text fontSize={{ base: 'sm', md: "md" }} textAlign={"center"}>Klik tombol dibawah ini untuk melakukan Pembayaran</Text>

                                <Flex mt={1}>
                                    <Spacer />
                                    {
                                        (order.status === 10 || order.status === 40) && <Button colorScheme={'red'} fontSize={"xl"} mt={3} py={8} px={8} isLoading={isSubmiting} onClick={onSubmit}> Bayar Sekarang </Button>
                                    }
                                    <Spacer />
                                </Flex>
                            </>
                        }

                        <Divider my={12} />

                        <Text mt={12} fontSize={{ base: 'sm', md: "md" }} textAlign={"center"}>Butuh Bantuan?</Text>
                        <Flex mt={1} mb={12}>
                            <Spacer />
                            <Button variant={"outline"} onClick={() => { window.open(AppConsts.UrlSupportTeam, "_blank") }}>Hubungi Team Support</Button>
                            <Spacer />
                        </Flex>
                    </Box >
                </GridItem>


                <GridItem bgColor={'blackAlpha.50'} rounded={'lg'} >
                    <PayProgram order={order} />
                </GridItem>

            </Grid>
        </>
    );
};

export default PayDetailScene;